import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const DISCORD_URL = "https://discord.gg/zHvehwznjc";

// Discord glyph — pure SVG replica in yellow, no emoji.
const DiscordGlyph = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.075.075 0 0 0-.079.037c-.212.375-.443.86-.6 1.247a18.288 18.288 0 0 0-5.487 0c-.157-.398-.4-.872-.612-1.247a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 5.9 4.369a.07.07 0 0 0-.032.027C2.9 8.766 2.2 13.05 2.548 17.28a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.224-1.99.076.076 0 0 0-.041-.105 13.117 13.117 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.073.073 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.128 12.313 12.313 0 0 1-1.873.891.077.077 0 0 0-.041.106c.36.698.772 1.363 1.225 1.99a.076.076 0 0 0 .084.028 19.834 19.834 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-4.87-.838-9.117-3.548-12.884a.061.061 0 0 0-.031-.028zM8.02 14.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.094 2.157 2.418 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.947 2.42-2.157 2.42z"
      fill="#FFD400"
    />
  </svg>
);

// Modal shown the first time the player buys the "Comunidad Cósmica" upgrade.
// Encourages them to join the official Discord server.
export const DiscordInviteModal = ({ open, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(DISCORD_URL);
      setCopied(true);
      toast("Discord link copied", { description: DISCORD_URL });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("Could not copy link");
    }
  };

  const openDiscord = () => {
    window.open(DISCORD_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
          onClick={onClose}
          data-testid="discord-invite-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.94 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="relative w-full max-w-md p-8 flex flex-col items-center text-center"
            style={{
              backgroundColor: "#0d0d0d",
              border: "2px solid #FFD400",
              borderRadius: 4,
              boxShadow: "0 0 80px rgba(255, 212, 0, 0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="discord-invite-modal"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="discord-invite-close"
              className="absolute top-3 right-3 text-white/60 hover:text-white p-1"
            >
              <X size={16} />
            </button>

            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                border: "2px solid #FFD400",
                boxShadow: "0 0 24px rgba(255,212,0,0.4)",
              }}
            >
              <DiscordGlyph size={36} />
            </div>

            <div
              className="mt-5 text-[10px] uppercase tracking-[0.4em]"
              style={{ color: "#FFD400", fontFamily: "Orbitron, sans-serif" }}
            >
              Cosmic Community Unlocked
            </div>

            <h2
              className="mt-2 text-2xl sm:text-3xl font-black uppercase tracking-widest text-white"
              style={{
                fontFamily: "Orbitron, sans-serif",
                textShadow: "0 0 20px rgba(255,212,0,0.5)",
              }}
              data-testid="discord-invite-title"
            >
              Join the Discord
            </h2>

            <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-sm">
              Has llegado al pináculo del imperio de las ruedas. Join the Discord
              oficial de BendyWheels para participar en eventos, nuevas eras,
              ranking global y contenido exclusivo.
            </p>

            <div
              className="mt-5 w-full flex items-center gap-2 p-3"
              style={{
                backgroundColor: "#111",
                border: "1px solid #FFD400",
                borderRadius: 2,
              }}
            >
              <span
                className="flex-1 truncate text-xs text-white/80"
                style={{ fontFamily: "monospace" }}
                data-testid="discord-invite-url"
              >
                {DISCORD_URL}
              </span>
              <button
                type="button"
                onClick={copy}
                aria-label="Copy link"
                data-testid="discord-invite-copy"
                className="text-white/60 hover:text-[#FFD400] flex-shrink-0"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>

            <button
              type="button"
              onClick={openDiscord}
              data-testid="discord-invite-open"
              className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 font-black uppercase tracking-[0.25em] text-sm transition-colors hover:bg-white"
              style={{
                backgroundColor: "#FFD400",
                color: "#111",
                fontFamily: "Orbitron, sans-serif",
                borderRadius: 2,
              }}
            >
              <ExternalLink size={14} />
              Open Discord
            </button>

            <button
              type="button"
              onClick={onClose}
              data-testid="discord-invite-later"
              className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white/70"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              Later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DiscordInviteModal;
